import Icon from 'components/AppIcon';
import { SIDEBAR_NAV, SIDEBAR_SETTINGS_LINK } from 'config/sidebarNavConfig';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

function splitPath(path) {
    const [pathname, hash = ''] = path.split('#');
    return { pathname, hash: hash ? `#${hash}` : '' };
}

function findActiveGroup(pathname, hash = '') {
    return SIDEBAR_NAV.find((group) =>
        group.children.some((child) => {
            const { pathname: childPath } = splitPath(child.path);
            return pathname === childPath
                || pathname.startsWith(`${childPath}/`);
        })
    ) ?? null;
}

function Sidebar({
    variant = 'expanded',
    className = '',
    isMobileOpen: isMobileOpenProp,
    onMobileToggle,
    onToggle,
    ...props
}) {
    const [isCollapsed, setIsCollapsed] = useState(variant === 'collapsed');
    const [internalMobileOpen, setInternalMobileOpen] = useState(false);
    const [openCollapsedGroup, setOpenCollapsedGroup] = useState(null);
    const isMobileOpen = isMobileOpenProp ?? internalMobileOpen;
    const [expandedGroup, setExpandedGroup] = useState(() => {
        const active = findActiveGroup(window.location.pathname, window.location.hash);
        return active?.name ?? null;
    });
    const location = useLocation();

    useEffect(() => {
        const active = findActiveGroup(location.pathname, location.hash);
        if (active) {
            setExpandedGroup(active.name);
        }
    }, [location.pathname, location.hash]);

    const isChildActive = (child, pathname = location.pathname, hash = location.hash) => {
        const { pathname: childPath, hash: childHash } = splitPath(child.path);

        if (pathname !== childPath) {
            return false;
        }

        if (childHash) {
            return hash === childHash;
        }

        if (child.end) {
            return !hash;
        }

        return true;
    };

    const isGroupActive = (group) =>
        group.children.some((child) => {
            const { pathname: childPath } = splitPath(child.path);
            return location.pathname === childPath
                || location.pathname.startsWith(`${childPath}/`);
        });

    const setMobileOpen = (open) => {
        if (onMobileToggle) {
            onMobileToggle(open);
        } else {
            setInternalMobileOpen(open);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setMobileOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [onMobileToggle]);

    const toggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        if (!newState) {
            setOpenCollapsedGroup(null);
        }
        if (onToggle) onToggle(newState);
    };

    const toggleMobile = () => {
        setMobileOpen(!isMobileOpen);
    };

    const handleNavClick = () => {
        if (window.innerWidth < 768) {
            setMobileOpen(false);
        }
    };

    const toggleGroup = (groupName) => {
        setExpandedGroup((prev) => (prev === groupName ? null : groupName));
    };

    const linkClassName = (isActive, nested = false) => `
        flex items-center py-2 text-sm font-medium rounded-md
        ${nested ? 'pl-9 pr-2' : 'px-2'}
        ${isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'}
        ${isCollapsed && !nested ? 'justify-center' : ''}
    `;

    const isSettingsActive =
        location.pathname === SIDEBAR_SETTINGS_LINK.path
        || location.pathname.startsWith(`${SIDEBAR_SETTINGS_LINK.path}/`);

    const renderSettingsLink = () => (
        <Link
            to={SIDEBAR_SETTINGS_LINK.path}
            className={linkClassName(isSettingsActive)}
            title={isCollapsed ? SIDEBAR_SETTINGS_LINK.name : undefined}
            aria-current={isSettingsActive ? 'page' : undefined}
            onClick={handleNavClick}
        >
            <Icon
                name={SIDEBAR_SETTINGS_LINK.icon}
                size={20}
                className={isSettingsActive ? 'text-primary-600' : 'text-gray-500'}
            />
            {!isCollapsed && <span className="ml-3">{SIDEBAR_SETTINGS_LINK.name}</span>}
        </Link>
    );

    const renderGroupItem = (group, { mobile = false } = {}) => {
        const isExpanded = expandedGroup === group.name;
        const groupActive = isGroupActive(group);

        if (isCollapsed && !mobile) {
            const isFlyoutOpen = openCollapsedGroup === group.name;

            return (
                <div key={group.name} className="relative">
                    <button
                        type="button"
                        onClick={() => setOpenCollapsedGroup(isFlyoutOpen ? null : group.name)}
                        className={linkClassName(groupActive)}
                        title={group.name}
                        aria-expanded={isFlyoutOpen}
                        aria-haspopup="true"
                    >
                        <Icon
                            name={group.icon}
                            size={20}
                            className={groupActive ? 'text-primary-600' : 'text-gray-500'}
                        />
                    </button>

                    {isFlyoutOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-40"
                                onClick={() => setOpenCollapsedGroup(null)}
                                aria-hidden="true"
                            />
                            <div className="absolute left-full top-0 z-50 ml-1 w-52 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                                <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    {group.name}
                                </p>
                                {group.children.map((child) => {
                                    const isActive = isChildActive(child);

                                    return (
                                        <Link
                                            key={child.name}
                                            to={child.path}
                                            className={`block px-3 py-2 text-sm font-medium rounded-md mx-1
                                                ${isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'}`}
                                            aria-current={isActive ? 'page' : undefined}
                                            onClick={() => setOpenCollapsedGroup(null)}
                                        >
                                            {child.name}
                                        </Link>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            );
        }

        return (
            <div key={group.name}>
                <button
                    type="button"
                    onClick={() => toggleGroup(group.name)}
                    className={`
                        w-full flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md
                        ${groupActive ? 'text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'}
                    `}
                    aria-expanded={isExpanded}
                >
                    <span className="flex items-center min-w-0">
                        <Icon
                            name={group.icon}
                            size={20}
                            className={groupActive ? 'text-primary-600' : 'text-gray-500'}
                        />
                        <span className="ml-3 truncate">{group.name}</span>
                    </span>
                    <Icon
                        name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                        size={16}
                        className="text-gray-400 flex-shrink-0"
                    />
                </button>

                {isExpanded && (
                    <div className="mt-1 space-y-1">
                        {group.children.map((child) => {
                            const isActive = isChildActive(child);

                            return (
                                <Link
                                    key={child.name}
                                    to={child.path}
                                    className={linkClassName(isActive, true)}
                                    aria-current={isActive ? 'page' : undefined}
                                    onClick={handleNavClick}
                                >
                                    <span>{child.name}</span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const sidebarWidthClass = isCollapsed ? 'w-16' : 'w-64';

    const mobileSidebarClasses = isMobileOpen
        ? 'translate-x-0 ease-out' : '-translate-x-full ease-in';

    const desktopSidebar = (
        <div
            className={`
        hidden md:flex md:flex-col md:fixed md:inset-y-0 
        bg-white border-r border-gray-200
        transition-width duration-300 ease-in-out
        ${sidebarWidthClass}
        ${className}
      `}
            {...props}
        >
            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                <div
                    className={`flex items-center h-16 flex-shrink-0 px-4 border-b border-gray-200 ${isCollapsed ? "justify-center" : ""
                        }`}
                >
                    <Link to="/analytics/overview" className="flex items-center">
                        <div className="text-primary-600 w-8 h-8 bg-primary-600">
                            <img
                                src="/tmpj5uq7cal.webp"
                                alt="Logo"
                                className="w-full h-full"
                            />
                        </div>
                        {!isCollapsed && (
                            <span className="ml-2 text-xl font-bold text-gray-900">
                                Best Version 1
                            </span>
                        )}
                    </Link>
                </div>

                <nav className="flex-1 px-2 py-4 space-y-1">
                    {SIDEBAR_NAV.map(renderGroupItem)}
                </nav>

                <div className="border-t border-gray-200 px-2 py-3">
                    {renderSettingsLink()}
                </div>

                <div className={`p-4 border-t border-gray-200 ${isCollapsed ? 'flex justify-center' : ''}`}>
                    <button
                        type="button"
                        onClick={toggleCollapse}
                        className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                    </button>
                </div>
            </div>
        </div>
    );

    const mobileSidebar = (
        <>
            {isMobileOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-gray-600 bg-opacity-75 z-20"
                    onClick={toggleMobile}
                    aria-hidden="true"
                ></div>
            )}

            <div
                className={`
          md:hidden fixed inset-y-0 left-0 flex flex-col z-30 w-72 max-w-xs
          bg-white shadow-xl transform transition-transform duration-300
          ${mobileSidebarClasses}
        `}
            >
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                    <div className="flex items-center justify-between h-16 flex-shrink-0 px-4 border-b border-gray-200">
                        <Link to="/analytics/overview" className="flex items-center">
                            <div className="text-primary-600">
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 9C8 7.34315 9.34315 6 11 6H21C22.6569 6 24 7.34315 24 9V23C24 24.6569 22.6569 26 21 26H11C9.34315 26 8 24.6569 8 23V9Z" fill="currentColor" />
                                    <path d="M14 13C14 11.8954 14.8954 11 16 11C17.1046 11 18 11.8954 18 13V23C18 24.1046 17.1046 25 16 25C14.8954 25 14 24.1046 14 23V13Z" fill="white" />
                                </svg>
                            </div>
                            <span className="ml-2 text-xl font-bold text-gray-900">Best Version 1</span>
                        </Link>
                        <button
                            type="button"
                            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            onClick={toggleMobile}
                            aria-label="Close sidebar"
                        >
                            <Icon name="X" size={20} />
                        </button>
                    </div>

                    <nav className="flex-1 px-2 py-4 space-y-1">
                        {SIDEBAR_NAV.map((group) => renderGroupItem(group, { mobile: true }))}
                    </nav>

                    <div className="border-t border-gray-200 px-4 py-3">
                        {renderSettingsLink()}
                    </div>

                    <div className="border-t border-gray-200 p-4">
                        <Link
                            to="/login"
                            className="flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-red-600 hover:bg-red-50"
                            onClick={() => {
                                handleNavClick();
                                localStorage.removeItem("authToken");
                                localStorage.removeItem("isAdmin");
                            }}
                        >
                            <Icon name="LogOut" size={20} className="text-gray-500 mr-3" />
                            Log out
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <>
            {desktopSidebar}
            {mobileSidebar}
        </>
    );
}

export default Sidebar;
